import {apiClient, queryClient} from '@common/http/query-client';
import {localizationsBaseKey} from '@common/admin/translations/localizations-queries';
import {loadLocaleLines} from '@ui/i18n/locale-lines-store';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {queryOptions} from '@tanstack/react-query';
import type {Lines} from './translation-editor-model';

export interface TranslationEditorResponse {
  localization: {id: number; name: string; language: string; direction: 'ltr' | 'rtl'};
  source: Lines;
  package: Lines;
  edits: Lines;
  version: string;
}

export const translationEditorKey = (id: number) => [...localizationsBaseKey, id, 'editor'];

export const translationEditorOptions = (id: number) =>
  queryOptions({
    queryKey: translationEditorKey(id),
    queryFn: () =>
      apiClient
        .get<TranslationEditorResponse>(`localizations/${id}/editor`)
        .then(r => r.data),
    staleTime: 0,
  });

export function saveTranslationLines(id: number, lines: Lines, mode: 'merge' | 'replace') {
  return apiClient
    .put<{changed: number; total_edits: number; version: string; edits: Lines}>(
      `localizations/${id}/lines`,
      {lines, mode},
    )
    .then(r => r.data);
}

export function resetTranslationLines(id: number) {
  return apiClient
    .post<{removed: number; version: string}>(`localizations/${id}/reset-lines`)
    .then(r => r.data);
}

/** After a save: refresh the editor data and, if it is the language in use, the live UI. */
export async function afterLinesChanged(id: number, language: string, version: string) {
  await queryClient.invalidateQueries({queryKey: translationEditorKey(id)});
  const i18n = getBootstrapData().i18n;
  if (i18n?.active === language) {
    const base = (getBootstrapData().settings.base_url || '').replace(/\/$/, '');
    await loadLocaleLines(language, `${base}/i18n/${language}.json?v=${version}`);
  }
}
